import { TGetRSODto, TStudyLinkStatus } from "@/api/services/main/rso/dtos/get-rso.dto";
import {
  cancelRsoStudyLinkRequestApi,
  createRsoStudyLinkRequestApi,
  getVisaStudyAccessApprovalUrl,
  syncVisaInformationApi,
  unlinkRsoStudyLinkApi,
  useSyncRsoStudyLink,
} from "@/api/services/main/rso";
import { TVisaActionType, VisaLinkSection } from "@/components/commons/VisaLinkSection";
import { TVisaLinkSubmitPayload, VisaLinkModal } from "@/components/commons/VisaLinkModal";
import { VisaLinkRequireFieldModal } from "@/components/commons/VisaLinkRequireFieldModal";
import { VisaApprovalUrlModal } from "@/components/commons/VisaApprovalUrlModal";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { useDialog } from "@/hooks/useDialog";
import { RSO_LICENSE_NUMBER_ERROR_MESSAGE, RSO_LICENSE_NUMBER_REGEX } from "@/features/rso/schema/rsoSchema";
import { showError } from "@/utils/error";
import { FCC } from "@/types/common";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { toast } from "@/utils/toast";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { AppRoutes } from "@/routes/config";
import { useNavigate } from "react-router-dom";

const UNLINK_VISA_TALENT_COUNT_TEXT = Array(4).fill("-").join("");

const visaApiMap: Record<Exclude<TVisaActionType, "link">, () => Promise<unknown>> = {
  unlink: unlinkRsoStudyLinkApi,
  sync: syncVisaInformationApi,
  cancel: cancelRsoStudyLinkRequestApi,
};

// 連携ステータスの表示ラベル。接続中を最優先し、それ以外は直近申請のステータスで表示する。
const NOT_LINKED_STATUS_LABEL = "連携なし";
const STUDY_LINK_STATUS_LABEL: Record<TStudyLinkStatus, string> = {
  pending: "連携認証待ち",
  approved: "連携中",
  rejected: "連携なし",
  expired: "連携なし",
  cancelled: "連携なし",
};

type TRsoVisaLinkSectionProps = {
  data: Partial<TGetRSODto>;
  isEditMode?: boolean;
};

export const RsoVisaLinkSection: FCC<TRsoVisaLinkSectionProps> = ({ data, isEditMode = false }) => {
  const studyLink = data.studyLink;
  // 有効な連携 (rso_links) があれば連携済み。なければ直近申請のステータスを見る。
  const isLinked = !!studyLink?.isConnected;
  // 連携済みは有効連携 (isConnected) のみで判定する。連携解除後は申請ステータスが approved のまま残るため、
  // 未連携時の approved は「連携なし」とみなす (pending/rejected/expired のみ申請ステータスを表示)。
  const statusValue = isLinked
    ? STUDY_LINK_STATUS_LABEL.approved
    : studyLink?.status && studyLink.status !== "approved"
      ? STUDY_LINK_STATUS_LABEL[studyLink.status]
      : NOT_LINKED_STATUS_LABEL;
  // 認証待ちの申請がある場合は、再申請を防ぐため連携セットアップを無効化する。
  const isPendingApproval = !isLinked && studyLink?.status === "pending";
  // 最終同期日は連携日時 (connectedAt) を表示する。
  const lastSyncDate = studyLink?.connectedAt ?? null;
  const queryClient = useQueryClient();
  const [activeAction, setActiveAction] = useState<TVisaActionType | null>(null);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [isRequireInfoModalOpen, setIsRequireInfoModalOpen] = useState(false);
  // 認証ページの URL。自動でタブを開かず、確認モーダル経由でユーザー操作で開く。
  const [pendingApprovalUrl, setPendingApprovalUrl] = useState<string | null>(null);
  const unlinkConfirm = useDialog();
  const cancelConfirm = useDialog();
  const navigate = useNavigate();

  // 認証待ちの間は BE 経由で Visa と状態を同期し続ける。
  // 同期結果が pending 以外 (承認・却下・期限切れ) になったら RSO 詳細を再取得して UI を更新する。
  const { data: syncedLink } = useSyncRsoStudyLink(isPendingApproval);
  useEffect(() => {
    if (syncedLink && syncedLink.status !== "pending") {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RSO.FETCH_DETAIL] });
    }
  }, [syncedLink, queryClient]);

  // 連携には登録番号の入力が必須
  const isMissingLicenseNumber = !data.licenseNumber?.trim();

  const handleSetupClick = () => {
    if (isMissingLicenseNumber) {
      setIsRequireInfoModalOpen(true);
      return;
    }
    setIsLinkModalOpen(true);
  };

  const handleVisaAction = async (type: Exclude<TVisaActionType, "link">) => {
    try {
      setActiveAction(type);
      await visaApiMap[type]();
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RSO.FETCH_DETAIL] });
      toast.success(API_SUCCESS_MESSAGE);
    } catch (error) {
      showError(error);
    } finally {
      setActiveAction(null);
    }
  };

  // 連携解除は不可逆な操作のため、実行前に確認モーダルで同意を得る。
  const handleUnlinkClick = async () => {
    const agree = await unlinkConfirm.show();
    if (!agree) return;
    await handleVisaAction("unlink");
  };

  // 認証待ちの申請取り消しも確認モーダルで同意を得てから実行する。
  const handleCancelClick = async () => {
    const agree = await cancelConfirm.show();
    if (!agree) return;
    await handleVisaAction("cancel");
  };

  // 連携リクエストを送信する。エラー時は再入力できるよう VisaLinkModal 側で再 throw を捕捉する。
  const handleLinkSubmit = async (payload: TVisaLinkSubmitPayload) => {
    try {
      const { data } = await createRsoStudyLinkRequestApi(payload);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RSO.FETCH_DETAIL] });
      if (payload.authType === "self") {
        // self: 認証モーダルを表示し、同意操作で認証ページを開く (toast は出さない)。
        const approvalUrl = data?.publicId ? getVisaStudyAccessApprovalUrl(data.publicId) : null;
        if (approvalUrl) setPendingApprovalUrl(approvalUrl);
      } else {
        // delegate: BE が担当者へ認証依頼メールを送る。
        toast.success("担当者へ認証依頼メールを送信しました");
      }
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  return (
    <>
      <VisaLinkSection
        title="irohana visa 連携設定"
        description="irohana visaと連携することで、特定技能外国人を学習者として簡単に取り込みや同期が可能になります。"
        statusValue={statusValue}
        lastSyncDate={lastSyncDate}
        visaCount={{
          label: "irohana visaの外国人数",
          value: isLinked ? `${studyLink?.visaEmployeeCount ?? 0}` : UNLINK_VISA_TALENT_COUNT_TEXT,
          tooltip: "特定技能1号および特定活動の内定または在籍人数のみ集計しています。",
        }}
        studyCount={{
          label: "irohana studyの学習者数",
          value: data.studyLearnerCount != null ? `${data.studyLearnerCount}` : undefined,
          tooltip: "受験ステータスが進行中である受講生のみ集計しています。（合格済みや不合格終了となった受講生はカウントしていません）",
        }}
        note="初回連携時は、irohana visaに登録支援機関としての契約、および有効なユーザーによる認証・承認が必要になります。"
        isLinked={isLinked}
        isEditMode={isEditMode}
        activeAction={activeAction}
        isPendingApproval={isPendingApproval}
        onUnlink={handleUnlinkClick}
        onSync={() => handleVisaAction("sync")}
        onSetup={handleSetupClick}
        onCancelRequest={handleCancelClick}
      />
      <ConfirmDeleteModal
        open={unlinkConfirm.open}
        title="連携解除の確認"
        message="irohana visa との連携を解除してもよろしいですか？"
        onClose={unlinkConfirm.cancel}
        onOk={unlinkConfirm.confirm}
      />
      <ConfirmDeleteModal
        open={cancelConfirm.open}
        title="連携申請の取り消し確認"
        message="irohana visa への連携申請 (認証待ち) を取り消してもよろしいですか？"
        onClose={cancelConfirm.cancel}
        onOk={cancelConfirm.confirm}
      />
      <VisaLinkModal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        onOk={handleLinkSubmit}
        licenseNumberPattern={RSO_LICENSE_NUMBER_REGEX}
        licenseNumberErrorMessage={RSO_LICENSE_NUMBER_ERROR_MESSAGE}
      />
      <VisaLinkRequireFieldModal
        isOpen={isRequireInfoModalOpen}
        fieldLabel="登録番号"
        onClose={() => setIsRequireInfoModalOpen(false)}
        onNavigate={() => {
          setIsRequireInfoModalOpen(false);
          navigate(AppRoutes.rsoEdit);
        }}
      />
      <VisaApprovalUrlModal approvalUrl={pendingApprovalUrl} onClose={() => setPendingApprovalUrl(null)} />
    </>
  );
};
